import React, { useState, createElement } from "react";
import { gql } from "apollo-boost";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { Spin, Row, Col, Alert } from "antd";
import { RouteComponentProps } from "react-router-dom";
import { CONSTANTS as CONSTANTSData } from "./__generated__/CONSTANTS";
import {
  SCRULER as SCRULERData,
  SCRULERVariables,
} from "./__generated__/SCRULER";
import moment from "moment";
import { SCSelect } from "./components/SCSelect";
import { Mining } from "./components/Mining";
import { Factories } from "./components/Factories";
import { Research } from "./components/Research";
import { Farming } from "./components/Farming";
import { Priority } from "./components/Priority";
import { Technology } from "./components/Technology";
import { Disband } from "./components/Disband";
import { User } from "../../lib/types";
import formatNumber from "../../lib/utils/formatNumber";
import {
  SYSTEMORBIT as SYSTEMORBITData,
  SYSTEMORBITVariables,
} from "./__generated__/SYSTEMORBIT";
import {
  updateSC as updateSCData,
  updateSCVariables,
} from "./__generated__/updateSC";
import { SC as SCData, SCVariables } from "./__generated__/SC";

import _ from "lodash";

const SYSTEMORBIT = gql`
  query SYSTEMORBIT($system: Int, $orbit: String) {
    systemOrbit(system: $system, orbit: $orbit) {
      orbits {
        deposits {
          number
          amount
          type
          percent
        }
      }
    }
  }
`;

const CONSTANTS = gql`
  query CONSTANTS {
    constants {
      turnMS
      rulers
      updated
      touched
      updateMS
    }
  }
`;

const UPDATE_SC = gql`
  mutation updateSC($sc: ID!) {
    updateSC(sc: $sc) {
      sc
      player
      ruler
      type
      system
      orbit
      tx
      ty
      tz
      sol
      uskPay
      sldPay
      proPay
      birthRate
      deathRate
      rations
      cngdStock
      foodStock
      pop {
        type
        race
        amount
      }
      asm {
        item
        tl
        amount
      }
      una {
        item
        tl
        amount
      }
      non {
        item
        tl
        amount
      }
      updated
      touched
      workGroups {
        group
        amount
        item
        tl
        status
        deposit
        outputItem
        outputTL
        wip
      }
      alerts {
        key
        message
        banner
      }
      vitals {
        key
        message
      }
      scIdentity {
        ruler
        number
        name
      }
    }
  }
`;

const SC = gql`
  query SC($sc: [ID!]!) {
    sc(sc: $sc) {
      sc
      player
      ruler
      type
      system
      orbit
      tx
      ty
      tz
      sol
      uskPay
      sldPay
      proPay
      birthRate
      deathRate
      rations
      cngdStock
      foodStock
      pop {
        type
        race
        amount
      }
      asm {
        item
        tl
        amount
      }
      una {
        item
        tl
        amount
      }
      non {
        item
        tl
        amount
      }
      updated
      touched
      workGroups {
        group
        amount
        item
        tl
        status
        deposit
        outputItem
        outputTL
        wip
      }
      alerts {
        message
        key
        banner
      }
      vitals {
        key
        message
      }
      scIdentity {
        ruler
        number
        name
      }
      drfQueue {
        pop
        race
        amount
        original
      }
      cnwQueue {
        target
        item
        tl
        order
        deposit
        outputItem
        outputTL
        amount
        original
      }
      tptQueue {
        target
        item
        tl
        order
        deposit
        outputItem
        outputTL
        amount
        original
      }
    }
  }
`;

const SCRULER = gql`
  query SCRULER($ruler: [Int!]!) {
    ruler(ruler: $ruler) {
      tech {
        item
        tl
      }
    }
  }
`;

interface Props {
  user: User;
}

interface MatchParams {
  id: string;
}

export const SCPage = ({
  user,
  history,
  match,
}: Props & RouteComponentProps<MatchParams>) => {
  let thisSC = Array(match.params.id);

  const [system, setSystem] = useState<number | null>(null);
  const [orbit, setOrbit] = useState<string | null>(null);

  const systemOrbitQuery = useQuery<SYSTEMORBITData, SYSTEMORBITVariables>(
    SYSTEMORBIT,
    {
      variables: { system, orbit },
    }
  );

  const constantLookup = useQuery<CONSTANTSData>(CONSTANTS);
  const rulerLookup = useQuery<SCRULERData, SCRULERVariables>(SCRULER, {
    variables: {
      ruler: [user["http://empyreanchallenge.com/app_metadata"].ruler],
    },
    fetchPolicy: "network-only",
  });
  const [update, { called }] = useMutation<updateSCData, updateSCVariables>(
    UPDATE_SC,
    { variables: { sc: thisSC[0] } }
  );

  const doUpdate = async () => {
    await update();
    refetch();
  };

  const { data, loading, error, refetch } = useQuery<SCData, SCVariables>(SC, {
    variables: { sc: thisSC },
    fetchPolicy: "network-only",
  });

  const sc = data && data.sc ? data.sc : null;
  const onlySC = sc ? _.first(sc) : undefined;

  const constants = constantLookup.data ? constantLookup.data : null;

  const rulers =
    rulerLookup.data && rulerLookup.data.ruler ? rulerLookup.data.ruler : null;
  const onlyRuler = rulers ? _.first(rulers) : undefined;

  const systemOrbit = systemOrbitQuery.data
    ? systemOrbitQuery.data.systemOrbit
    : null;

  const thisOrbit =
    systemOrbit && systemOrbit.orbits && systemOrbit.orbits.length > 0
      ? _.first(systemOrbit.orbits)
      : null;
  const deposits = thisOrbit ? thisOrbit.deposits : null;

  if (onlySC && system === null && orbit === null) {
    setSystem(onlySC.system);
    setOrbit(onlySC.orbit);
    return <Spin size="large" />;
  }

  if (
    loading ||
    constantLookup.loading ||
    systemOrbitQuery.loading ||
    rulerLookup.loading
  ) {
    return <Spin size="large" />;
  }

  if (!called) {
    doUpdate();
  }

  if (
    error ||
    constantLookup.error ||
    systemOrbitQuery.error ||
    rulerLookup.error
  ) {
    if (error) {
      console.log(error);
    }
    if (constantLookup.error) {
      console.log(constantLookup.error);
    }
    if (systemOrbitQuery.error) {
      console.log(systemOrbitQuery.error);
    }
    if (rulerLookup.error) {
      console.log(rulerLookup.error);
    }

    return <h2>Error Loading Data!</h2>;
  }

  const onChangeSelect = async function (value: string, option: any) {
    const newPath = option.key;
    history.push(newPath);
  };

  if (
    onlySC &&
    onlySC.ruler !== user["http://empyreanchallenge.com/app_metadata"].ruler &&
    onlySC.player !== user["http://empyreanchallenge.com/app_metadata"].player
  ) {
    return (
      <>
        <SCSelect user={user} value={undefined} onSelect={onChangeSelect} />
        <h2>Access Denied.</h2>
      </>
    );
  }

  var children: JSX.Element[] = [];

  function displayItems(groupName: string, p: any[] | null) {
    let entries: JSX.Element[] = [];

    children.push(<br key={groupName + "break"} />);
    children.push(<h3 key={groupName}>{groupName}</h3>);

    if (p) {
      const sorted = p.sort((a, b) =>
        a.item > b.item ? 1 : a.item === b.item ? (a.tl > b.tl ? 1 : -1) : -1
      );
      sorted.forEach((q) => {
        const item = q.tl === 0 ? q.item : q.item + "-" + q.tl;
        entries.push(
          <Col
            key={item}
            style={{ textAlign: "right" }}
            xs={{ span: 24 }}
            sm={{ span: 12 }}
            md={{ span: 8 }}
            lg={{ span: 6 }}
            xl={{ span: 4 }}
            xxl={{ span: 2 }}
          >
            {formatNumber(q.amount)} {item}
          </Col>
        );
      });
    } else {
      children.push(
        <Col key={"No " + groupName} span={24} style={{ textAlign: "center" }}>
          No {groupName}!
        </Col>
      );
    }

    children.push(<Row key={groupName + "Row"}>{entries}</Row>);
  }

  if (onlySC && constants) {
    if (onlySC.alerts && onlySC.alerts.length > 0) {
      _.forEach(onlySC.alerts, function ({ key, message, banner }) {
        if (key && message) {
          children.push(
            createElement(Alert, {
              key,
              message,
              banner,
              closable: true,
            })
          );
        }
      });
    }

    const lastUpdate = moment(onlySC.updated);
    children.push(
      <h2 key={onlySC.sc}>
        Last Update: {lastUpdate.format("YYYY-MM-DD @ hh:mm a")}
      </h2>
    );

    children.push(<br key="Vitals break" />);
    children.push(<h3 key="Vitals">Vital Statistics</h3>);
    let vitals: JSX.Element[] = [];

    vitals.push(
      <Col
        key="sol"
        style={{ textAlign: "right" }}
        xs={{ span: 24 }}
        sm={{ span: 12 }}
        md={{ span: 8 }}
        lg={{ span: 6 }}
        xl={{ span: 4 }}
        xxl={{ span: 2 }}
      >
        Standard of Living: {onlySC.sol.toFixed(3)}
      </Col>
    );
    vitals.push(
      <Col
        key="birth"
        style={{ textAlign: "right" }}
        xs={{ span: 24 }}
        sm={{ span: 12 }}
        md={{ span: 8 }}
        lg={{ span: 6 }}
        xl={{ span: 4 }}
        xxl={{ span: 2 }}
      >
        Birth Rate: {(onlySC.birthRate * 100).toFixed(4)}%
      </Col>
    );
    vitals.push(
      <Col
        key="death"
        style={{ textAlign: "right" }}
        xs={{ span: 24 }}
        sm={{ span: 12 }}
        md={{ span: 8 }}
        lg={{ span: 6 }}
        xl={{ span: 4 }}
        xxl={{ span: 2 }}
      >
        Death Rate: {(onlySC.deathRate * 100).toFixed(4)}%
      </Col>
    );
    vitals.push(
      <Col
        key="usk"
        style={{ textAlign: "right" }}
        xs={{ span: 24 }}
        sm={{ span: 12 }}
        md={{ span: 8 }}
        lg={{ span: 6 }}
        xl={{ span: 4 }}
        xxl={{ span: 2 }}
      >
        USK Pay: {onlySC.uskPay.toFixed(3)}
      </Col>
    );
    vitals.push(
      <Col
        key="sld"
        style={{ textAlign: "right" }}
        xs={{ span: 24 }}
        sm={{ span: 12 }}
        md={{ span: 8 }}
        lg={{ span: 6 }}
        xl={{ span: 4 }}
        xxl={{ span: 2 }}
      >
        SLD Pay: {onlySC.sldPay.toFixed(3)}
      </Col>
    );
    vitals.push(
      <Col
        key="pro"
        style={{ textAlign: "right" }}
        xs={{ span: 24 }}
        sm={{ span: 12 }}
        md={{ span: 8 }}
        lg={{ span: 6 }}
        xl={{ span: 4 }}
        xxl={{ span: 2 }}
      >
        PRO Pay: {onlySC.proPay.toFixed(3)}
      </Col>
    );
    vitals.push(
      <Col
        key="rations"
        style={{ textAlign: "right" }}
        xs={{ span: 24 }}
        sm={{ span: 12 }}
        md={{ span: 8 }}
        lg={{ span: 6 }}
        xl={{ span: 4 }}
        xxl={{ span: 2 }}
      >
        Rations: {onlySC.rations.toFixed(3)}
      </Col>
    );

    let totalPop = 0;
    _.forEach(onlySC.pop, ({ amount }) => {
      totalPop += Math.floor(amount);
    });

    vitals.push(
      <Col
        key="totalPop"
        style={{ textAlign: "right" }}
        xs={{ span: 24 }}
        sm={{ span: 12 }}
        md={{ span: 8 }}
        lg={{ span: 6 }}
        xl={{ span: 4 }}
        xxl={{ span: 2 }}
      >
        Population: {formatNumber(totalPop)}
      </Col>
    );

    _.forEach(onlySC.vitals, ({ key, message }) => {
      vitals.push(
        createElement(
          Col,
          {
            key,
            style: { textAlign: "right" },
            xs: { span: 24 },
            sm: { span: 12 },
            md: { span: 8 },
            lg: { span: 6 },
            xl: { span: 4 },
            xxl: { span: 2 },
          },
          message
        )
      );
    });

    children.push(<Row key="Vitals Row">{vitals}</Row>);
    children.push(<br key="Census break" />);
    children.push(<h3 key={"census"}>Census Report</h3>);
    let entries: JSX.Element[] = [];

    if (onlySC.pop) {
      const sorted = onlySC.pop.sort((a, b) =>
        a.type > b.type
          ? 1
          : a.type === b.type
          ? a.race > b.race
            ? 1
            : -1
          : -1
      );

      sorted.forEach((q) => {
        entries.push(
          <Col
            key={q.type + q.race}
            style={{ textAlign: "right" }}
            xs={{ span: 24 }}
            sm={{ span: 12 }}
            md={{ span: 8 }}
            lg={{ span: 6 }}
            xl={{ span: 4 }}
            xxl={{ span: 2 }}
          >
            {formatNumber(q.amount)} {q.type}-{q.race}
          </Col>
        );
      });
    } else {
      children.push(
        <Col key={"No pop"} span={24} style={{ textAlign: "center" }}>
          No Population!
        </Col>
      );
    }

    children.push(<Row key="Census Row">{entries}</Row>);

    displayItems("Non-Assembly Items", onlySC.non);
    displayItems("Unassembled Items", onlySC.una);
    displayItems("Assembled Items", onlySC.asm);

    if (onlySC.workGroups) {
      const mines = onlySC.workGroups.filter((value) => value.item === "MIN");

      if (mines.length > 0) {
        if (deposits !== null) {
          children.push(
            <Mining
              key="Mining Report"
              sc={thisSC[0]}
              deposits={deposits}
              mines={mines}
              reload={refetch}
            />
          );
        }
      }

      const factories = onlySC.workGroups.filter(
        (value) => value.item === "FCT"
      );
      if (factories.length > 0) {
        children.push(
          <Factories
            key="Factory Report"
            sc={thisSC[0]}
            factories={factories}
            reload={refetch}
          />
        );
      }

      const labs = onlySC.workGroups.filter((value) => value.item === "LAB");
      if (labs.length > 0) {
        children.push(
          <Research
            key="Research Report"
            sc={thisSC[0]}
            labs={labs}
            reload={refetch}
          />
        );

        const farms = onlySC.workGroups.filter((value) => value.item === "FRM");
        if (farms.length > 0) {
          children.push(
            <Farming
              key="Farming Report"
              sc={thisSC[0]}
              farms={farms}
              reload={refetch}
            />
          );
        }

        children.push(
          <Priority
            key="Priority Change"
            sc={thisSC[0]}
            workGroups={onlySC.workGroups}
            reload={refetch}
          />
        );

        if (onlyRuler) {
          if (onlySC.non) {
            const rschEntry = onlySC.non.filter(
              (value) => value.item === "RSCH"
            );
            if (rschEntry) {
              children.push(
                <Technology
                  key="Technology Report"
                  sc={onlySC}
                  tech={onlyRuler.tech}
                  reload={refetch}
                  reload2={rulerLookup.refetch}
                />
              );
            }
          }
        }

        if (onlySC.drfQueue) {
          if (onlySC.drfQueue.length > 0) {
            children.push(
              <Disband
                key="Disband Report"
                sc={onlySC}
                drfQueues={onlySC.drfQueue}
                reload={refetch}
              />
            );
          }
        }
      }
    }
    children.push(<br key="Bottom break" />);
  } else {
    children = [];
  }

  return (
    <>
      <SCSelect user={user} value={thisSC[0]} onSelect={onChangeSelect} />
      {children}
    </>
  );
};
